import { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownListItem, FInter, FlexBox, useBreakpoint } from '@brg/ui';
import { ProfileUtils, useAssociatedProfile } from 'core/auth';

import { LinksItems } from '../../../components/slideNavigation/slideNavigation';
import {
  DesignerTab,
  PROFILE_TABS_IN_ORDER,
  PROFILE_TABS_ME_IN_ORDER,
  PROFILE_TABS_WORKING_MATERIALS
} from '../../../constants/designerTab';
import ArrowTopIconWithText from 'shared/icons/ArrowTopIconWithText';
import styled, { css, useTheme } from 'styled-components';
import { LayoutConst } from 'core';
import { ApprovedIcon } from 'shared/icons/ApprovedIcon';
import { HseContextActions } from "../../../reducers/hseCurrentContext";
import { useDispatch } from "react-redux";
import { useServiceLinks } from 'shared/hooks/useServiceLinks';
import { Designer } from '@brg/api';
import { DesignerSliderItem } from './designerSliderItem';
import { AppTester } from "../../../../supporting";
import useQueryState from 'shared/hooks/useQueryState';

export const MobileSlider = styled(FlexBox)`
  width: 100%;
  justify-content: flex-start;
  place-items: center;
  margin: 0 0;
`;

export const DropdownText = styled(FInter.SmallTextBoldMagic)<{ isActive?: boolean }>`
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${p => p?.isActive ? p.theme.icon.active : p.theme.icon.hoverSpecialActive};

    // @media(max-width: ${LayoutConst.screen.l_16}px) {
  //   font-size: 15px;
  //   line-height: 20px;
  // }

    // @media (max-width: ${LayoutConst.screen.md}px) {
  //   font-size: 13px;
  // }
`;

const SliderWrapper = styled(FlexBox)<{isGameDeziiign?: boolean, dropdownHeight?: number, showScroll?: boolean}>`
  position: relative;
  
  .dropdown-show {
    height: auto;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    >div{
      overflow: ${({showScroll}) => showScroll ? 'auto' : 'visible'} !important;
      ${({showScroll}) => showScroll && `
        &::-webkit-scrollbar {
            height: 1px;
            width: 2px;
          }
          &::-webkit-scrollbar-track {
            border-radius: 10px;
            background: ${({theme}: any) => theme.competition.input.bgActive} !important;
          }
          &::-webkit-scrollbar-thumb {
            background: ${({theme}: any) => theme.competition.input.inputText} !important;
          }
      `}
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px){
      >div{
        overflow: auto !important;
      }
      
      * {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
        
        &::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
      }
    }

    ${({ showScroll, theme }) => showScroll && `
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 40px;
        background: linear-gradient(to top, ${theme.navigationBar.login.dropDownBackground} 0%, ${theme.navigationBar.login.dropDownBackground}00 50% );
        pointer-events: none;
        z-index: 1;
      }
    `}

    @media (max-width: ${LayoutConst.screen.s - 1}px){
      height: ${({dropdownHeight}) => dropdownHeight ? `${dropdownHeight}px` : '224px'};
    }
  }
  .target-icon-wrapper {
    .dropdown-header {
      font-family: 'Inter';
      font-style: normal;
      font-weight: 600;
      font-size: 15px;
      line-height: 20px;
        // color: ${({ theme }) => theme.icon.default};

    }

    svg path {
      fill: ${({ theme }) => theme.icon.default};
    }
  }

  .dropdown-list-item {
    height: 40px;
    background: ${({theme}: any) => theme.navigationBar.login.dropDownBackground};

    &:hover {
      background: ${({ theme }) => theme.navigationBar.login.dropDownHoverBackground};

      div {
        color: ${({ theme }) => theme.viewType.item.hover};
      }
        
      span{
        color: ${({ theme }) => theme.viewType.item.hover};
      }
    }
    div, span {
      font-size: 15px;
      line-height: 20px;
    }    
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    .dropdown-header, .dropdown-list-item {
        text-transform: lowercase;
    }
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    .dropdown-list-item {
        text-transform: lowercase;
        ${({isGameDeziiign}) => isGameDeziiign && css`
                background: #3F4548;
                color: #8E9193;
                &:hover{
                    background: #575B5E;
                    color: #fefefe;
                }
        `}
    }
  }

`;

export const applyCountToString = (s: Record<string, number>, key: string, text: string) => (
  key in s && s[key] && s[key] > 0 ? `${text} | ${s[key]}` : text
);

export interface DesignerSliderProps {
  activeTabKey: string;
  onTabChange: (indexKey: string, addTab?: string) => void;
  counts: Record<string, number>;
  additionalCount: Record<string, number>;
  isMe?: boolean;
  designer?: Designer;
  whiteTheme?: any;
  overflowX?: boolean;
  tabInsDropdown?: boolean;
  addTab?: string;
  isLoading?: boolean;
  noProjects?: any;
}

export const DesignerSlider: FC<DesignerSliderProps> = ({
  activeTabKey,
  onTabChange,
  counts,
  additionalCount,
  isMe,
  designer,
  whiteTheme,
  overflowX,
  tabInsDropdown,
  isLoading,
  addTab,
  noProjects,
}) => {
  const profile = useAssociatedProfile();
  const [ {showcase = false }] = useQueryState();
  const theme = useTheme();
  const isMobile = useBreakpoint(LayoutConst.screen.m);
  const dispatch = useDispatch();
  const serviceLinks = useServiceLinks(designer?.designerId ?? '');
  const isSuperPuper = AppTester.isSuperPuper();
  const isAgeytomesh = AppTester.isAgeytomesh();
  const isDvaDesign = AppTester.isDvaDesign();
  const isMedia = AppTester.isMediiia();
  const isGameDeziiign = AppTester.isGameDeziiign();
  const isModer = useMemo(() => (ProfileUtils.isModer(profile) || ProfileUtils.isSuperModer(profile)), [profile]);
  const is744BP = useBreakpoint(LayoutConst.screen.s);
  const is743 = useBreakpoint(LayoutConst.screen.s -1 );

  const { t } = useTranslation();

  const itemsRef = useRef(false);
  useEffect(() => {
      if(Object.keys(counts).length){
          itemsRef.current = true;
      }
  }, [counts])

  const oneLineSorting = useMemo(
    () => (
      designer &&
      (designer.globalTypes?.includes('Student') &&
        !designer.globalTypes?.includes('Employee') &&
        !designer.globalTypes?.includes('Cooperator') &&
        !designer.globalTypes?.includes('hsealumni'))
    )
    , [designer]);

  const additionalTabs = useMemo(() => {
    const tabs: Record<string, string> = {};

    if (!!Object.keys(additionalCount)?.length) {
      if (additionalCount[DesignerTab.workingMaterialsAuthor] > 0) {
        //@ts-ignore
        tabs[DesignerTab.workingMaterialsAuthor] = applyCountToString(additionalCount, DesignerTab.workingMaterialsAuthor, t('author'));
      }
      if (additionalCount[DesignerTab.workingMaterialsCoauthor] > 0) {
        //@ts-ignore
        tabs[DesignerTab.workingMaterialsCoauthor] = applyCountToString(additionalCount, DesignerTab.workingMaterialsCoauthor, t('co_author'));
      }

      if (additionalCount[DesignerTab.workingMaterialsStudents] > 0) {
        //@ts-ignore
        tabs[DesignerTab.workingMaterialsStudents] = applyCountToString(additionalCount, DesignerTab.workingMaterialsStudents, t('students'));
      }

      if (additionalCount[DesignerTab.subscriptionsProjects] === 0 /*&& isMe*/) {
        tabs[DesignerTab.subscriptionsProjects] = applyCountToString(additionalCount, DesignerTab.subscriptionsProjects, t(`${is743 ? 'mobile_' : ''}subscriptionsProjects`))
      }
      if (additionalCount[DesignerTab.subscriptions] > 0 ) {
        tabs[DesignerTab.subscriptions] = applyCountToString(additionalCount, DesignerTab.subscriptions, t(`${isMe ? 'my_' : ''}${is743 ? 'mobile_' : ''}subscriptions`))
      }
      if (additionalCount[DesignerTab.subscribers] > 0) {
        tabs[DesignerTab.subscribers] = applyCountToString(additionalCount, DesignerTab.subscribers, t(`${isMe ? 'my_' : ''}${is743 ? 'mobile_' : ''}subscribers`))
      }
      if (additionalCount[DesignerTab.educationSubscribes] > 0) {
        tabs[DesignerTab.educationSubscribes] = applyCountToString(additionalCount, DesignerTab.educationSubscribes, t(`educationSubscribes`))
      }

      return tabs;
    }
    return undefined;
  }, [additionalCount, activeTabKey, isMe, t, is743])

  const tabItems = useMemo(() => {
    const tabs: Record<string, string | LinksItems> = {};

    if (isSuperPuper) {
      tabs[DesignerTab.games_archive] = applyCountToString(
        counts,
        DesignerTab.games_archive,
        t(DesignerTab.games_archive),
      );

      if (isMe) {
        tabs[DesignerTab.games_drafts] = applyCountToString(
          counts,
          DesignerTab.games_drafts,
          t(DesignerTab.games_drafts),
        );
      }
      return tabs;
    }

    Object.values(PROFILE_TABS_IN_ORDER).forEach((item, index, array) => {
      if (isMobile && item === DesignerTab.workingMaterials) {
        Object.values(PROFILE_TABS_WORKING_MATERIALS).forEach((item) => {
          if (counts[item] > 0) {
            tabs[item] = applyCountToString(
              counts,
              item,
              t(item),
            );
          }
        });
      } else if ((item === activeTabKey && showcase) || counts[item] > 0) {
        tabs[item] = applyCountToString(
          counts,
          item,
          t(item),
        );
      } 
      else if (item === DesignerTab.subscribe &&(counts['subscribers'] > 0 || counts['subscriptions'] > 0 || counts['educationSubscribes'] > 0)) {
        tabs[item] = applyCountToString(
          counts,
          item,
          t(item),
        );
      } 
    })
    
    if (serviceLinks && is743) {
      Object.assign(tabs, serviceLinks)
    }

    if (isMe) {
      Object.values(PROFILE_TABS_ME_IN_ORDER).forEach((item) => {
        if ((isAgeytomesh || isDvaDesign) && [DesignerTab.lms, DesignerTab.lmsDrafts, DesignerTab.drafts, DesignerTab.draftBooks]?.includes(item)) {
          return;
        }
        if (counts[item] > 0) {
          tabs[item] = applyCountToString(
            counts,
            item,
            t(item),
          );
        }
      })
    }

    if (counts[DesignerTab.author] === 0 && !isMobile && !isMe && Object.keys(tabs)?.length === 1) {
      return {...{[DesignerTab.author]: `${t(DesignerTab.author)} | 0`}, ...tabs};
    }

    return tabs;
  }, [t, itemsRef.current, counts, isMe, is743, profile, isModer, isMobile, is744BP, serviceLinks, showcase, isSuperPuper]);

  useEffect(() => {
    const tabs = Object.fromEntries(Object.entries(tabItems)?.map(([k, v]) => {
      return !['artsWorks', 'gallery']?.includes(k) ? [k, v] : []
    })?.filter(([k, v]) => typeof k !== 'undefined'))
    const titles = Object.values(tabItems)?.filter((i: any) => typeof i === 'string');
    dispatch(HseContextActions.set({
      tabs: tabs,
      customTitles: titles,
      initialTab: isSuperPuper ? tabItems.games_archive : tabItems.author,
      onSetTab: onTabChange,
    }));
    return () => {
      dispatch(HseContextActions.reset({}));
    }
  }, [tabItems, isSuperPuper])

  const mobileAdditionalTabs = useMemo(() => {

    console.log('1111',additionalTabs, tabInsDropdown)
    if (activeTabKey !== DesignerTab.subscribe) return;

    if (additionalTabs && tabInsDropdown) {
      return (
        <MobileSlider className='designer-mobile-slider'>
          {Object.entries(additionalTabs)?.map(([k,v]) => {
            return (
              <DropdownText
                isActive={k === addTab} 
                style={{ marginRight: 8, whiteSpace: 'nowrap' }} 
                onClick={() => onTabChange(DesignerTab.subscribe, k)}
              >
                {v.toLowerCase()}
              </DropdownText>
            )
          })}
          <DropdownText></DropdownText>
        </MobileSlider>
      )
    }
  } , [additionalTabs, tabInsDropdown, activeTabKey, addTab ])

  // Вычисляем количество элементов в dropdown
  const dropdownItemsCount = useMemo(() => {
    if (!tabInsDropdown) return 0;
    
    const items = Object.keys(tabItems).filter(key => {
      const hideList = ['competitions', 'bookmarks', 'drafts', 'draft_books', 'rejected', 'banned_projects', 'lms', 'lms_drafts'];
      return !(hideList.includes(key) && !isMe);
    });
    
    return items.length;
  }, [tabItems, tabInsDropdown, isMe]);

  const { dropdownHeight, showScroll } = useMemo(() => {
    if (!tabInsDropdown || dropdownItemsCount === 0) {
      return { dropdownHeight: 224, showScroll: true };
    }
    
    const itemHeight = 40;
    const maxHeight = 224;
    const calculatedHeight = dropdownItemsCount * itemHeight;
    
    if (dropdownItemsCount <= 5) {
      return { dropdownHeight: calculatedHeight, showScroll: false };
    }
    
    return { dropdownHeight: maxHeight, showScroll: true };
  }, [dropdownItemsCount, tabInsDropdown]);

  if (!Object.keys(tabItems)?.length) {
    return null;
  }

  return (
    <SliderWrapper
      width="100%"
      {...tabInsDropdown && { direction: 'column'}}
      widthMobile={tabInsDropdown ? 'max-content' : '100%'}
      heightMobile={tabInsDropdown ? '100%' : 'auto'}
      justifyMobile={tabInsDropdown ? 'end' : 'start'}
      alignMobile={tabInsDropdown && !Object.keys(additionalCount)?.length ? 'center' : 'start'}
      isGameDeziiign={isGameDeziiign}
      dropdownHeight={dropdownHeight}
      showScroll={showScroll}
      className={'disegner-slider'}
    >
      {tabInsDropdown ? (
        <Dropdown
          offsetY={0}
          dark={!whiteTheme}
          hideArrow
          orientation={'left'}
          maxHeight={dropdownHeight}
          target={
            <ArrowTopIconWithText
              //@ts-ignore
              text={noProjects?.all ? t('tabs_show_more') : tabItems[activeTabKey]}
              active
              shrimpIcon
              hideGradient
              newColorStyle
            />
          }
        >
          {Object.keys(tabItems)?.map((key) => {
              const isLink = typeof tabItems[key] !== 'string';
              const hideList = ['competitions', 'bookmarks', 'drafts', 'draft_books', 'rejected', 'banned_projects', 'lms', 'lms_drafts']?.reverse();

              if (hideList?.includes(key) && !isMe) return false;

              return (
                <div key={key} onClick={() => {
                  if (key === activeTabKey) return false;
                  //@ts-ignore
                  if (isLink) return tabItems[key]?.action()
                  onTabChange(key);
                }}>
                  <DropdownListItem
                    justify='space-between'
                    isActive={key === activeTabKey}
                  >
                    <DropdownText isActive={key === activeTabKey}>
                      {/*@ts-ignore*/}
                      {isLink ? tabItems[key]?.element() : tabItems[key]}
                    </DropdownText>
                    <ApprovedIcon fill={theme.icon.active} display={key === activeTabKey}/>
                  </DropdownListItem>
                </div>
              )
            }
          )}
        </Dropdown>
      ) : (
        <DesignerSliderItem
          items={tabItems}
          additionalItems={additionalTabs}
          activeIndexKey={activeTabKey}
          onChange={onTabChange}
          whiteTheme={whiteTheme}
          overflowX={overflowX}
          isLoading={isLoading}
          isMe={isMe}
          oneLineSorting={oneLineSorting}
          addTab={addTab}
        />
      )}
    {mobileAdditionalTabs}
    </SliderWrapper>
  );
};

export default DesignerSliderimport { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownListItem, FInter, FlexBox, useBreakpoint } from '@brg/ui';
import { ProfileUtils, useAssociatedProfile } from 'core/auth';

import { LinksItems } from '../../../components/slideNavigation/slideNavigation';
import {
  DesignerTab,
  PROFILE_TABS_IN_ORDER,
  PROFILE_TABS_ME_IN_ORDER,
  PROFILE_TABS_WORKING_MATERIALS
} from '../../../constants/designerTab';
import ArrowTopIconWithText from 'shared/icons/ArrowTopIconWithText';
import styled, { css, useTheme } from 'styled-components';
import { LayoutConst } from 'core';
import { ApprovedIcon } from 'shared/icons/ApprovedIcon';
import { HseContextActions } from "../../../reducers/hseCurrentContext";
import { useDispatch } from "react-redux";
import { useServiceLinks } from 'shared/hooks/useServiceLinks';
import { Designer } from '@brg/api';
import { DesignerSliderItem } from './designerSliderItem';
import { AppTester } from "../../../../supporting";
import useQueryState from 'shared/hooks/useQueryState';

export const MobileSlider = styled(FlexBox)`
  width: 100%;
  justify-content: flex-start;
  place-items: center;
  margin: 0 0;
`;

export const DropdownText = styled(FInter.SmallTextBoldMagic)<{ isActive?: boolean }>`
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${p => p?.isActive ? p.theme.icon.active : p.theme.icon.hoverSpecialActive};

    // @media(max-width: ${LayoutConst.screen.l_16}px) {
  //   font-size: 15px;
  //   line-height: 20px;
  // }

    // @media (max-width: ${LayoutConst.screen.md}px) {
  //   font-size: 13px;
  // }
`;

const SliderWrapper = styled(FlexBox)<{isGameDeziiign?: boolean, dropdownHeight?: number, showScroll?: boolean}>`
  position: relative;
  
  .dropdown-show {
    height: auto;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    >div{
      overflow: ${({showScroll}) => showScroll ? 'auto' : 'visible'} !important;
      ${({showScroll}) => showScroll && `
        &::-webkit-scrollbar {
            height: 1px;
            width: 2px;
          }
          &::-webkit-scrollbar-track {
            border-radius: 10px;
            background: ${({theme}: any) => theme.competition.input.bgActive} !important;
          }
          &::-webkit-scrollbar-thumb {
            background: ${({theme}: any) => theme.competition.input.inputText} !important;
          }
      `}
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px){
      >div{
        overflow: auto !important;
      }
      
      * {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
        
        &::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
      }
    }

    ${({ showScroll, theme }) => showScroll && `
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 40px;
        background: linear-gradient(to top, ${theme.navigationBar.login.dropDownBackground} 0%, ${theme.navigationBar.login.dropDownBackground}00 50% );
        pointer-events: none;
        z-index: 1;
      }
    `}

    @media (max-width: ${LayoutConst.screen.s - 1}px){
      height: ${({dropdownHeight}) => dropdownHeight ? `${dropdownHeight}px` : '224px'};
    }
  }
  .target-icon-wrapper {
    .dropdown-header {
      font-family: 'Inter';
      font-style: normal;
      font-weight: 600;
      font-size: 15px;
      line-height: 20px;
        // color: ${({ theme }) => theme.icon.default};

    }

    svg path {
      fill: ${({ theme }) => theme.icon.default};
    }
  }

  .dropdown-list-item {
    height: 40px;
    background: ${({theme}: any) => theme.navigationBar.login.dropDownBackground};

    &:hover {
      background: ${({ theme }) => theme.navigationBar.login.dropDownHoverBackground};

      div {
        color: ${({ theme }) => theme.viewType.item.hover};
      }
        
      span{
        color: ${({ theme }) => theme.viewType.item.hover};
      }
    }
    div, span {
      font-size: 15px;
      line-height: 20px;
    }    
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    .dropdown-header, .dropdown-list-item {
        text-transform: lowercase;
    }
  }
  @media (max-width: ${LayoutConst.screen.m}px) {
    .dropdown-list-item {
        text-transform: lowercase;
        ${({isGameDeziiign}) => isGameDeziiign && css`
                background: #3F4548;
                color: #8E9193;
                &:hover{
                    background: #575B5E;
                    color: #fefefe;
                }
        `}
    }
  }

`;

export const applyCountToString = (s: Record<string, number>, key: string, text: string) => (
  key in s && s[key] && s[key] > 0 ? `${text} | ${s[key]}` : text
);

export interface DesignerSliderProps {
  activeTabKey: string;
  onTabChange: (indexKey: string, addTab?: string) => void;
  counts: Record<string, number>;
  additionalCount: Record<string, number>;
  isMe?: boolean;
  designer?: Designer;
  whiteTheme?: any;
  overflowX?: boolean;
  tabInsDropdown?: boolean;
  addTab?: string;
  isLoading?: boolean;
  noProjects?: any;
}

export const DesignerSlider: FC<DesignerSliderProps> = ({
  activeTabKey,
  onTabChange,
  counts,
  additionalCount,
  isMe,
  designer,
  whiteTheme,
  overflowX,
  tabInsDropdown,
  isLoading,
  addTab,
  noProjects,
}) => {
  const profile = useAssociatedProfile();
  const [ {showcase = false }] = useQueryState();
  const theme = useTheme();
  const isMobile = useBreakpoint(LayoutConst.screen.m);
  const dispatch = useDispatch();
  const serviceLinks = useServiceLinks(designer?.designerId ?? '');
  const isSuperPuper = AppTester.isSuperPuper();
  const isAgeytomesh = AppTester.isAgeytomesh();
  const isDvaDesign = AppTester.isDvaDesign();
  const isMedia = AppTester.isMediiia();
  const isGameDeziiign = AppTester.isGameDeziiign();
  const isModer = useMemo(() => (ProfileUtils.isModer(profile) || ProfileUtils.isSuperModer(profile)), [profile]);
  const is744BP = useBreakpoint(LayoutConst.screen.s);
  const is743 = useBreakpoint(LayoutConst.screen.s -1 );

  const { t } = useTranslation();

  const itemsRef = useRef(false);
  useEffect(() => {
      if(Object.keys(counts).length){
          itemsRef.current = true;
      }
  }, [counts])

  const oneLineSorting = useMemo(
    () => (
      designer &&
      (designer.globalTypes?.includes('Student') &&
        !designer.globalTypes?.includes('Employee') &&
        !designer.globalTypes?.includes('Cooperator') &&
        !designer.globalTypes?.includes('hsealumni'))
    )
    , [designer]);

  const additionalTabs = useMemo(() => {
    const tabs: Record<string, string> = {};

    if (!!Object.keys(additionalCount)?.length) {
      if (additionalCount[DesignerTab.workingMaterialsAuthor] > 0) {
        //@ts-ignore
        tabs[DesignerTab.workingMaterialsAuthor] = applyCountToString(additionalCount, DesignerTab.workingMaterialsAuthor, t('author'));
      }
      if (additionalCount[DesignerTab.workingMaterialsCoauthor] > 0) {
        //@ts-ignore
        tabs[DesignerTab.workingMaterialsCoauthor] = applyCountToString(additionalCount, DesignerTab.workingMaterialsCoauthor, t('co_author'));
      }

      if (additionalCount[DesignerTab.workingMaterialsStudents] > 0) {
        //@ts-ignore
        tabs[DesignerTab.workingMaterialsStudents] = applyCountToString(additionalCount, DesignerTab.workingMaterialsStudents, t('students'));
      }

      if (additionalCount[DesignerTab.subscriptionsProjects] === 0 /*&& isMe*/) {
        tabs[DesignerTab.subscriptionsProjects] = applyCountToString(additionalCount, DesignerTab.subscriptionsProjects, t(`${is743 ? 'mobile_' : ''}subscriptionsProjects`))
      }
      if (additionalCount[DesignerTab.subscriptions] > 0 ) {
        tabs[DesignerTab.subscriptions] = applyCountToString(additionalCount, DesignerTab.subscriptions, t(`${isMe ? 'my_' : ''}${is743 ? 'mobile_' : ''}subscriptions`))
      }
      if (additionalCount[DesignerTab.subscribers] > 0) {
        tabs[DesignerTab.subscribers] = applyCountToString(additionalCount, DesignerTab.subscribers, t(`${isMe ? 'my_' : ''}${is743 ? 'mobile_' : ''}subscribers`))
      }
      if (additionalCount[DesignerTab.educationSubscribes] > 0) {
        tabs[DesignerTab.educationSubscribes] = applyCountToString(additionalCount, DesignerTab.educationSubscribes, t(`educationSubscribes`))
      }

      return tabs;
    }
    return undefined;
  }, [additionalCount, activeTabKey, isMe, t, is743])

  const tabItems = useMemo(() => {
    const tabs: Record<string, string | LinksItems> = {};

    if (isSuperPuper) {
      tabs[DesignerTab.games_archive] = applyCountToString(
        counts,
        DesignerTab.games_archive,
        t(DesignerTab.games_archive),
      );

      if (isMe) {
        tabs[DesignerTab.games_drafts] = applyCountToString(
          counts,
          DesignerTab.games_drafts,
          t(DesignerTab.games_drafts),
        );
      }
      return tabs;
    }

    Object.values(PROFILE_TABS_IN_ORDER).forEach((item, index, array) => {
      if (isMobile && item === DesignerTab.workingMaterials) {
        Object.values(PROFILE_TABS_WORKING_MATERIALS).forEach((item) => {
          if (counts[item] > 0) {
            tabs[item] = applyCountToString(
              counts,
              item,
              t(item),
            );
          }
        });
      } else if ((item === activeTabKey && showcase) || counts[item] > 0) {
        tabs[item] = applyCountToString(
          counts,
          item,
          t(item),
        );
      } 
      else if (item === DesignerTab.subscribe &&(counts['subscribers'] > 0 || counts['subscriptions'] > 0 || counts['educationSubscribes'] > 0)) {
        tabs[item] = applyCountToString(
          counts,
          item,
          t(item),
        );
      } 
    })
    
    if (serviceLinks && is743) {
      Object.assign(tabs, serviceLinks)
    }

    if (isMe) {
      Object.values(PROFILE_TABS_ME_IN_ORDER).forEach((item) => {
        if ((isAgeytomesh || isDvaDesign) && [DesignerTab.lms, DesignerTab.lmsDrafts, DesignerTab.drafts, DesignerTab.draftBooks]?.includes(item)) {
          return;
        }
        if (counts[item] > 0) {
          tabs[item] = applyCountToString(
            counts,
            item,
            t(item),
          );
        }
      })
    }

    if (counts[DesignerTab.author] === 0 && !isMobile && !isMe && Object.keys(tabs)?.length === 1) {
      return {...{[DesignerTab.author]: `${t(DesignerTab.author)} | 0`}, ...tabs};
    }

    return tabs;
  }, [t, itemsRef.current, counts, isMe, is743, profile, isModer, isMobile, is744BP, serviceLinks, showcase, isSuperPuper]);

  useEffect(() => {
    const tabs = Object.fromEntries(Object.entries(tabItems)?.map(([k, v]) => {
      return !['artsWorks', 'gallery']?.includes(k) ? [k, v] : []
    })?.filter(([k, v]) => typeof k !== 'undefined'))
    const titles = Object.values(tabItems)?.filter((i: any) => typeof i === 'string');
    dispatch(HseContextActions.set({
      tabs: tabs,
      customTitles: titles,
      initialTab: isSuperPuper ? tabItems.games_archive : tabItems.author,
      onSetTab: onTabChange,
    }));
    return () => {
      dispatch(HseContextActions.reset({}));
    }
  }, [tabItems, isSuperPuper])

  const mobileAdditionalTabs = useMemo(() => {

    console.log('1111',additionalTabs, tabInsDropdown)
    if (activeTabKey !== DesignerTab.subscribe) return;

    if (additionalTabs && tabInsDropdown) {
      return (
        <MobileSlider className='designer-mobile-slider'>
          {Object.entries(additionalTabs)?.map(([k,v]) => {
            return (
              <DropdownText
                isActive={k === addTab} 
                style={{ marginRight: 8, whiteSpace: 'nowrap' }} 
                onClick={() => onTabChange(DesignerTab.subscribe, k)}
              >
                {v.toLowerCase()}
              </DropdownText>
            )
          })}
          <DropdownText></DropdownText>
        </MobileSlider>
      )
    }
  } , [additionalTabs, tabInsDropdown, activeTabKey, addTab ])

  // Вычисляем количество элементов в dropdown
  const dropdownItemsCount = useMemo(() => {
    if (!tabInsDropdown) return 0;
    
    const items = Object.keys(tabItems).filter(key => {
      const hideList = ['competitions', 'bookmarks', 'drafts', 'draft_books', 'rejected', 'banned_projects', 'lms', 'lms_drafts'];
      return !(hideList.includes(key) && !isMe);
    });
    
    return items.length;
  }, [tabItems, tabInsDropdown, isMe]);

  const { dropdownHeight, showScroll } = useMemo(() => {
    if (!tabInsDropdown || dropdownItemsCount === 0) {
      return { dropdownHeight: 224, showScroll: true };
    }
    
    const itemHeight = 40;
    const maxHeight = 224;
    const calculatedHeight = dropdownItemsCount * itemHeight;
    
    if (dropdownItemsCount <= 5) {
      return { dropdownHeight: calculatedHeight, showScroll: false };
    }
    
    return { dropdownHeight: maxHeight, showScroll: true };
  }, [dropdownItemsCount, tabInsDropdown]);

  if (!Object.keys(tabItems)?.length) {
    return null;
  }

  return (
    <SliderWrapper
      width="100%"
      {...tabInsDropdown && { direction: 'column'}}
      widthMobile={tabInsDropdown ? 'max-content' : '100%'}
      heightMobile={tabInsDropdown ? '100%' : 'auto'}
      justifyMobile={tabInsDropdown ? 'end' : 'start'}
      alignMobile={tabInsDropdown && !Object.keys(additionalCount)?.length ? 'center' : 'start'}
      isGameDeziiign={isGameDeziiign}
      dropdownHeight={dropdownHeight}
      showScroll={showScroll}
      className={'disegner-slider'}
    >
      {tabInsDropdown ? (
        <Dropdown
          offsetY={0}
          dark={!whiteTheme}
          hideArrow
          orientation={'left'}
          maxHeight={dropdownHeight}
          target={
            <ArrowTopIconWithText
              //@ts-ignore
              text={noProjects?.all ? t('tabs_show_more') : tabItems[activeTabKey]}
              active
              shrimpIcon
              hideGradient
              newColorStyle
            />
          }
        >
          {Object.keys(tabItems)?.map((key) => {
              const isLink = typeof tabItems[key] !== 'string';
              const hideList = ['competitions', 'bookmarks', 'drafts', 'draft_books', 'rejected', 'banned_projects', 'lms', 'lms_drafts']?.reverse();

              if (hideList?.includes(key) && !isMe) return false;

              return (
                <div key={key} onClick={() => {
                  if (key === activeTabKey) return false;
                  //@ts-ignore
                  if (isLink) return tabItems[key]?.action()
                  onTabChange(key);
                }}>
                  <DropdownListItem
                    justify='space-between'
                    isActive={key === activeTabKey}
                  >
                    <DropdownText isActive={key === activeTabKey}>
                      {/*@ts-ignore*/}
                      {isLink ? tabItems[key]?.element() : tabItems[key]}
                    </DropdownText>
                    <ApprovedIcon fill={theme.icon.active} display={key === activeTabKey}/>
                  </DropdownListItem>
                </div>
              )
            }
          )}
        </Dropdown>
      ) : (
        <DesignerSliderItem
          items={tabItems}
          additionalItems={additionalTabs}
          activeIndexKey={activeTabKey}
          onChange={onTabChange}
          whiteTheme={whiteTheme}
          overflowX={overflowX}
          isLoading={isLoading}
          isMe={isMe}
          oneLineSorting={oneLineSorting}
          addTab={addTab}
        />
      )}
    {mobileAdditionalTabs}
    </SliderWrapper>
  );
};

export default DesignerSlider